class EuroCaseShowcase extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-case-tab]'));
    this.tabPanels = Array.from(this.querySelectorAll('[data-case-tab-panel]'));
    this.gallerySwipers = new Map();
    this.activeTab = 0;
    this.activeCategory = new Map();

    this.onTabClick = (event) => {
      const index = Number(event.currentTarget.dataset.caseTab);
      if (Number.isNaN(index)) return;
      this.selectTab(index);
    };

    this.onCategoryClick = (event) => {
      const key = event.currentTarget.dataset.caseCategory;
      if (!key) return;
      this.selectCategory(key);
      this.closeDropdowns();
    };

    this.onTriggerClick = (event) => {
      const trigger = event.currentTarget;
      const tabIndex = trigger.dataset.caseCategoryTrigger;
      const dropdown = this.querySelector(`[data-case-dropdown="${tabIndex}"]`);
      if (!dropdown) return;

      const willOpen = dropdown.hasAttribute('hidden');
      this.closeDropdowns();
      if (willOpen) {
        dropdown.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
      }
    };

    this.onDocumentClick = (event) => {
      if (event.target.closest('.euro-case-showcase__mobile-bar')) return;
      this.closeDropdowns();
    };

    this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick));
    this.querySelectorAll('[data-case-category]').forEach((button) => {
      button.addEventListener('click', this.onCategoryClick);
    });
    this.querySelectorAll('[data-case-category-trigger]').forEach((button) => {
      button.addEventListener('click', this.onTriggerClick);
    });
    document.addEventListener('click', this.onDocumentClick);

    this.whenReady(() => {
      this.tabPanels.forEach((_, index) => this.activeCategory.set(index, '0'));
      this.selectTab(0, { animate: false });
      window.EuroAOS?.refresh();
    });
  }

  disconnectedCallback() {
    this.tabs?.forEach((tab) => tab.removeEventListener('click', this.onTabClick));
    this.querySelectorAll('[data-case-category]').forEach((button) => {
      button.removeEventListener('click', this.onCategoryClick);
    });
    this.querySelectorAll('[data-case-category-trigger]').forEach((button) => {
      button.removeEventListener('click', this.onTriggerClick);
    });
    document.removeEventListener('click', this.onDocumentClick);
    clearInterval(this.readyTimer);
    this.destroyGalleries();
  }

  whenReady(callback) {
    if (window.Swiper) {
      callback();
      return;
    }

    this.readyTimer = setInterval(() => {
      if (!window.Swiper) return;
      clearInterval(this.readyTimer);
      callback();
    }, 30);
  }

  closeDropdowns() {
    this.querySelectorAll('[data-case-dropdown]').forEach((dropdown) => {
      dropdown.hidden = true;
    });
    this.querySelectorAll('[data-case-category-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  selectTab(index) {
    this.activeTab = index;
    this.closeDropdowns();

    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });

    this.tabPanels.forEach((panel, i) => {
      const active = i === index;
      panel.classList.toggle('is-active', active);
      panel.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    const categoryIndex = this.activeCategory.get(index) || '0';
    this.selectCategory(`${index}-${categoryIndex}`, { force: true });
  }

  selectCategory(key, { force = false } = {}) {
    const [tabIndexText, categoryIndexText] = key.split('-');
    const tabIndex = Number(tabIndexText);
    if (Number.isNaN(tabIndex)) return;

    const current = this.activeCategory.get(tabIndex);
    if (!force && current === categoryIndexText && this.activeTab === tabIndex) return;

    this.activeCategory.set(tabIndex, categoryIndexText);

    const panel = this.tabPanels[tabIndex];
    if (!panel) return;

    panel.querySelectorAll('[data-case-category]').forEach((button) => {
      const active = button.dataset.caseCategory === key;
      button.classList.toggle('is-active', active);
      if (button.hasAttribute('aria-pressed')) {
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      }
    });

    panel.querySelectorAll('[data-case-category-panel]').forEach((article) => {
      const active = article.dataset.caseCategoryPanel === key;
      article.classList.toggle('is-active', active);
      article.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    const label = panel.querySelector('[data-case-category-label]');
    const activeNav = panel.querySelector(`.euro-case-showcase__nav-item[data-case-category="${key}"]`);
    if (label && activeNav) {
      label.textContent = activeNav.textContent.trim();
    }

    this.initGallery(key);
  }

  initGallery(key) {
    const el = this.querySelector(`[data-case-gallery="${key}"]`);
    if (!el || !window.Swiper) return;

    const existing = this.gallerySwipers.get(key);
    if (existing) {
      existing.update();
      return;
    }

    const slides = el.querySelectorAll('.swiper-slide');
    if (slides.length <= 1) return;

    const prev = this.querySelector(`[data-case-gallery-prev="${key}"]`);
    const next = this.querySelector(`[data-case-gallery-next="${key}"]`);

    const swiper = new window.Swiper(el, {
      slidesPerView: 1,
      speed: 400,
      watchOverflow: true,
      navigation: {
        prevEl: prev || undefined,
        nextEl: next || undefined,
      },
    });

    this.gallerySwipers.set(key, swiper);
    window.EuroAOS?.refresh();
  }

  destroyGalleries() {
    this.gallerySwipers.forEach((swiper) => swiper.destroy(true, true));
    this.gallerySwipers.clear();
  }
}

if (!customElements.get('euro-case-showcase')) {
  customElements.define('euro-case-showcase', EuroCaseShowcase);
}
